"use strict";
/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultQuoteItemRequestTypes = exports.DefaultQuoteActionType = exports.DefaultCartNoteAuthorType = exports.DefaultQuoteStatus = exports.DefaultCartStatus = exports.InventoryCheckStrategy = exports.InventoryReservationStrategy = exports.DefaultAddressSource = exports.DefaultCollectOnDeliveryStatus = exports.InventoryType = exports.DefaultFulfillmentType = exports.DefaultAlternateAdjustmentSource = exports.DefaultPricingStrategyType = exports.DefaultCartItemType = exports.TaxIncludedType = exports.DefaultUserTypes = exports.DefaultReviewerAssignmentTypes = exports.DefaultCartType = exports.CartRequestHeaderType = void 0;
var CartRequestHeaderType;
(function (CartRequestHeaderType) {
    CartRequestHeaderType["GUEST_TOKEN"] = "X-Guest-Token";
    CartRequestHeaderType["CART_VERSION"] = "X-Cart-Version";
})(CartRequestHeaderType || (exports.CartRequestHeaderType = CartRequestHeaderType = {}));
/**
 * Enumerates the types of carts.
 */
var DefaultCartType;
(function (DefaultCartType) {
    DefaultCartType["STANDARD"] = "STANDARD";
    DefaultCartType["QUOTE"] = "QUOTE";
})(DefaultCartType || (exports.DefaultCartType = DefaultCartType = {}));
/**
 * Enumerates the types of reviewer assignments.
 */
var DefaultReviewerAssignmentTypes;
(function (DefaultReviewerAssignmentTypes) {
    DefaultReviewerAssignmentTypes["QUOTE"] = "QUOTE";
})(DefaultReviewerAssignmentTypes || (exports.DefaultReviewerAssignmentTypes = DefaultReviewerAssignmentTypes = {}));
/**
 * Enumerates the types of users.
 */
var DefaultUserTypes;
(function (DefaultUserTypes) {
    /**
     * Represents a customer user who is an account member.
     */
    DefaultUserTypes["BLC_ACCOUNT_MEMBER"] = "BLC_ACCOUNT_MEMBER";
    /**
     * Represents a customer user who has permission to approve account carts.
     */
    DefaultUserTypes["BLC_ACCOUNT_APPROVER"] = "BLC_ACCOUNT_APPROVER";
    /**
     * Represents a customer.
     */
    DefaultUserTypes["BLC_CUSTOMER"] = "BLC_CUSTOMER";
    /**
     * Represents a sales representative.
     *
     * Typically used for users who can manage and respond to quotes.
     */
    DefaultUserTypes["BLC_SALES_REP"] = "BLC_SALES_REP";
    /**
     * Represents the system.
     *
     * Typically used for system originated changes such as a scheduled job to mark quotes as
     * expired.
     */
    DefaultUserTypes["BLC_SYSTEM"] = "BLC_SYSTEM";
})(DefaultUserTypes || (exports.DefaultUserTypes = DefaultUserTypes = {}));
var TaxIncludedType;
(function (TaxIncludedType) {
    /**
     * All taxes are included in the subtotal (VAT).
     */
    TaxIncludedType["YES"] = "YES";
    /**
     * No taxes are included in the subtotal.
     */
    TaxIncludedType["NO"] = "NO";
    /**
     * A fraction of the total tax amount is included in the subtotal (VAT), and the rest should be
     * added after the subtotal.
     */
    TaxIncludedType["PARTIAL"] = "PARTIAL";
})(TaxIncludedType || (exports.TaxIncludedType = TaxIncludedType = {}));
/**
 * The default types of cart items.
 */
var DefaultCartItemType;
(function (DefaultCartItemType) {
    /**
     * A normal cart item with typical behavior.
     */
    DefaultCartItemType["STANDARD"] = "STANDARD";
    /**
     * Represents a fee applied to the cart such as for Collect on Delivery or other payment
     * options. These are not to be included in the cart subtotal or quantity.
     */
    DefaultCartItemType["EXTRA_FEE"] = "EXTRA_FEE";
    /**
     * Represents a fee applied to the cart for Collect on Delivery. These are not to be included
     * in the cart subtotal or quantity.
     */
    DefaultCartItemType["COD_FEE"] = "COD_FEE";
    /**
     * Represents a quote item that is a custom item specified by the customer.
     *
     * This is used for supporting <<DefaultCartTypes>> `QUOTE` for buyers to specify any
     * custom items, e.g. training hours, additional service, or items that they couldn't find the
     * matching catalog items for.
     */
    DefaultCartItemType["CUSTOM_QUOTE_ITEM"] = "CUSTOM_QUOTE_ITEM";
})(DefaultCartItemType || (exports.DefaultCartItemType = DefaultCartItemType = {}));
/**
 * Pricing strategy for a dependent cart item. This is used to indicate whether to add
 * the price of the dependent item when calculating the subtotal amount of the cart.
 */
var DefaultPricingStrategyType;
(function (DefaultPricingStrategyType) {
    /**
     * The price of the dependent cart item will be added to the parent item's price.
     */
    DefaultPricingStrategyType["ADD_TO_PARENT"] = "ADD_TO_PARENT";
    /**
     * The price of the dependent cart item is considered to be already included in the
     * parent item's price.
     */
    DefaultPricingStrategyType["INCLUDED_IN_PARENT"] = "INCLUDED_IN_PARENT";
})(DefaultPricingStrategyType || (exports.DefaultPricingStrategyType = DefaultPricingStrategyType = {}));
/**
 * The default alternate adjustment source names.
 */
var DefaultAlternateAdjustmentSource;
(function (DefaultAlternateAdjustmentSource) {
    /**
     * This value indicates that the adjustment was added by the price proration of the bundle price
     * to its items.
     */
    DefaultAlternateAdjustmentSource["BUNDLE_ITEM_ADJUSTMENT"] = "BUNDLE_ITEM_ADJUSTMENT";
})(DefaultAlternateAdjustmentSource || (exports.DefaultAlternateAdjustmentSource = DefaultAlternateAdjustmentSource = {}));
/**
 * The default types of fulfillment for <<FulfillmentGroup, FulfillmentGroups>>.
 */
var DefaultFulfillmentType;
(function (DefaultFulfillmentType) {
    /**
     * This goes with <<InventoryType>> `NONE` and indicates that no fulfillment method is
     * required.
     */
    DefaultFulfillmentType["NONE"] = "NONE";
    /**
     * Indicates that an item will be shipped, often to a customer's home. This is applicable to
     * <<InventoryType>> `PHYSICAL` items.
     */
    DefaultFulfillmentType["SHIP"] = "SHIP";
    /**
     * This (a.k.a. SDD) is essentially the same as SHIP (with a SAME_DAY_DELIVERY service level).
     * This simply allows "SAME_DAY_DELIVERY" to be its own, special Fulfillment Method.
     */
    DefaultFulfillmentType["SAME_DAY_DELIVERY"] = "SAME_DAY_DELIVERY";
    /**
     * Indicates that an item will be picked up by the customer, often at a store or pickup
     * location. This is applicable to <<InventoryType>> `PHYSICAL` items. This is often known as
     * Buy-Online- Pickup-In-Store (or BOPIS).
     *
     * This should also be able to handle Delayed BOPIS, as the only things that may change are the
     * fulfillment dates and maybe the price.
     */
    DefaultFulfillmentType["PICKUP"] = "PICKUP";
    /**
     * Indicates that an item will be fulfilled virtually (e.g. via digital download, print, etc.)
     * This is applicable to <<InventoryType>> `VIRTUAL` items.
     */
    DefaultFulfillmentType["VIRTUAL"] = "VIRTUAL";
    /**
     * Indicates that an item's fulfillment has not yet been determined, e.g. custom quote item
     */
    DefaultFulfillmentType["TO_BE_DETERMINED"] = "TO_BE_DETERMINED";
})(DefaultFulfillmentType || (exports.DefaultFulfillmentType = DefaultFulfillmentType = {}));
/**
 * Used to distinguish the ways that a Product's/Variant's inventory is represented. This is used to
 * determine available fulfillment options & to split order items into different fulfillment groups
 * (e.g. `VIRTUAL` products will need to be fulfilled in a different way than `PHYSICAL`).
 */
var InventoryType;
(function (InventoryType) {
    InventoryType["NONE"] = "NONE";
    InventoryType["PHYSICAL"] = "PHYSICAL";
    InventoryType["VIRTUAL"] = "VIRTUAL";
})(InventoryType || (exports.InventoryType = InventoryType = {}));
/**
 * The default Collect on Delivery statuses possible for a <<FulfillmenGroup>>.
 * This is stored as an attribute.
 */
var DefaultCollectOnDeliveryStatus;
(function (DefaultCollectOnDeliveryStatus) {
    /**
     * Collect on Delivery is selected for this fulfillment group
     */
    DefaultCollectOnDeliveryStatus["SELECTED"] = "SELECTED";
    /**
     * Collect on Delivery is not selected for this fulfillment group
     */
    DefaultCollectOnDeliveryStatus["NOT_SELECTED"] = "NOT_SELECTED";
})(DefaultCollectOnDeliveryStatus || (exports.DefaultCollectOnDeliveryStatus = DefaultCollectOnDeliveryStatus = {}));
/**
 * Enumerates the sources where the address can be found for tax calculations.
 */
var DefaultAddressSource;
(function (DefaultAddressSource) {
    /**
     * The tax address should be populated from <<FulfillmentGroup>> `#address`.
     */
    DefaultAddressSource["SHIPPING_ADDRESS"] = "SHIPPING_ADDRESS";
    /**
     * The tax address should be populated from the billing address of the payment with the maximum
     * amount in the active payment.
     */
    DefaultAddressSource["PAYMENT_ADDRESS"] = "PAYMENT_ADDRESS";
})(DefaultAddressSource || (exports.DefaultAddressSource = DefaultAddressSource = {}));
/**
 * Enumerated values that describe when to trigger an inventory reservation.
 */
var InventoryReservationStrategy;
(function (InventoryReservationStrategy) {
    /**
     * Inventory should never be reserved.
     */
    InventoryReservationStrategy["NEVER"] = "NEVER";
    /**
     * Inventory should be reserved when the item is added to the cart.
     */
    InventoryReservationStrategy["ADD_TO_CART"] = "ADD_TO_CART";
    /**
     * Inventory should be reserved when the order is submitted for checkout.
     */
    InventoryReservationStrategy["SUBMIT_ORDER"] = "SUBMIT_ORDER";
})(InventoryReservationStrategy || (exports.InventoryReservationStrategy = InventoryReservationStrategy = {}));
/**
 * Enumerated values that describe when to trigger an inventory check.
 */
var InventoryCheckStrategy;
(function (InventoryCheckStrategy) {
    /**
     * Inventory should never be checked.
     */
    InventoryCheckStrategy["NEVER"] = "NEVER";
    /**
     * Inventory should be checked when the item is added to the cart.
     */
    InventoryCheckStrategy["ADD_TO_CART"] = "ADD_TO_CART";
})(InventoryCheckStrategy || (exports.InventoryCheckStrategy = InventoryCheckStrategy = {}));
/**
 * Enumerates the kinds of statuses that could be represented by a cart's status.
 */
var DefaultCartStatus;
(function (DefaultCartStatus) {
    /**
     * The <<Cart>> is in process and can be modified.
     */
    DefaultCartStatus["IN_PROCESS"] = "IN_PROCESS";
    /**
     * Used to represent a cart that requires approval. This is typically used for B2B account carts.
     */
    DefaultCartStatus["REQUIRES_APPROVAL"] = "REQUIRES_APPROVAL";
    /**
     * Used to represent a cart that has been rejected. This is typically used for B2B account carts.
     */
    DefaultCartStatus["REJECTED"] = "REJECTED";
    /**
     * Indicates that the <<Cart>> payment is sent to the payment gateway but awaiting
     * transaction result. Note that if one of the transactions is awaiting result in a
     * multiple-payment scenario, the cart would still have this status.
     */
    DefaultCartStatus["AWAITING_PAYMENT_RESULT"] = "AWAITING_PAYMENT_RESULT";
    /**
     * Indicates that the <<Cart>> had a pending payment but is now failed.
     * <p>
     * This can happen when the transaction result is pending during the checkout flow, but later
     * failed from receiving the latest transaction result.
     */
    DefaultCartStatus["PENDING_PAYMENT_FAILED"] = "PENDING_PAYMENT_FAILED";
    /**
     * Indicates that the <<Cart>> is actively being used by the scheduled job to check its
     * payment results and update the <<Cart>> accordingly.
     */
    DefaultCartStatus["PAYMENT_RESULT_CHECK_IN_PROGRESS"] = "PAYMENT_RESULT_CHECK_IN_PROGRESS";
    /**
     * Used to represent a cart that has been locked while its transactions requiring
     * reversal are being processed. Setting this status is primarily meant to ensure that no other
     * threads are able to simultaneously modify the cart.
     */
    DefaultCartStatus["LOCKED_FOR_TX_REVERSAL"] = "LOCKED_FOR_TX_REVERSAL";
    /**
     * Used to represent a cart that has been submitted by the customer & the processing of
     * that submission is in progress. While in this status, the cart should be locked from
     * modification by any customer requests.
     */
    DefaultCartStatus["SUBMISSION_IN_PROGRESS"] = "SUBMISSION_IN_PROGRESS";
    /**
     * Used to represent a cart that has been submitted by the customer & the processing of
     * that submission has been successfully completed. This means that the cart can no longer be
     * modified and is locked in. This also means that the cart should have its
     * `Cart#getOrderNumber` set.
     */
    DefaultCartStatus["SUBMITTED"] = "SUBMITTED";
    /**
     * The cart has been cancelled either by the customer, a CSR, or an automated process.
     */
    DefaultCartStatus["CANCELLED"] = "CANCELLED";
    /**
     * Represents a cart which is being managed by a CSR for the customer. A cart in this
     * status is not visible to the customer. This cart was either created by a CSR or cloned from
     * an existing customer's cart, and can be transferred back as the customer's IN_PROCESS cart.
     */
    DefaultCartStatus["CSR_OWNED"] = "CSR_OWNED";
    /**
     * Represents an anonymous cart which is primed to be transferred to a user. This cart
     * has no other uses prior to being transferred.
     */
    DefaultCartStatus["IN_TRANSFER"] = "IN_TRANSFER";
    /**
     * Represents a cart which is being managed by an admin in preview mode. This cart
     * cannot be submitted during checkout.
     */
    DefaultCartStatus["TEST"] = "TEST";
})(DefaultCartStatus || (exports.DefaultCartStatus = DefaultCartStatus = {}));
/**
 * Enumerates the statuses for a quote.
 */
var DefaultQuoteStatus;
(function (DefaultQuoteStatus) {
    /**
     * Indicates that the quote is being drafted and have not been submitted to the sellers.
     *
     * It's typically used for carts that have just been converted into a quote.
     */
    DefaultQuoteStatus["DRAFTING"] = "DRAFTING";
    /**
     * Indicates that the quote has been requested to the seller.
     */
    DefaultQuoteStatus["QUOTE_REQUESTED"] = "QUOTE_REQUESTED";
    /**
     * Indicates that the quote is assigned to a sales rep.
     */
    DefaultQuoteStatus["ASSIGNED"] = "ASSIGNED";
    /**
     * Indicates that the quote has been responded back to the requester with the quoted prices.
     */
    DefaultQuoteStatus["PUBLISHED"] = "PUBLISHED";
    /**
     * Indicates that the quote is being edited by the buyer after the seller's response.
     */
    DefaultQuoteStatus["EDITING"] = "EDITING";
    /**
     * Indicates that the quote is expired.
     */
    DefaultQuoteStatus["EXPIRED"] = "EXPIRED";
    /**
     * Indicates that the quote has been purchased.
     */
    DefaultQuoteStatus["PURCHASED"] = "PURCHASED";
    /**
     * Indicates that the quote is rejected.
     */
    DefaultQuoteStatus["REJECTED"] = "REJECTED";
    /**
     * Indicates that the quote is canceled.
     */
    DefaultQuoteStatus["CANCELED"] = "CANCELED";
})(DefaultQuoteStatus || (exports.DefaultQuoteStatus = DefaultQuoteStatus = {}));
/**
 * Enumerates the types of a <<CartNote,CartNote's>> author.
 */
var DefaultCartNoteAuthorType;
(function (DefaultCartNoteAuthorType) {
    /**
     * Represents a customer.
     */
    DefaultCartNoteAuthorType["CUSTOMER"] = "CUSTOMER";
    /**
     * Represents a sales representative.
     */
    DefaultCartNoteAuthorType["SALES_REP"] = "SALES_REP";
})(DefaultCartNoteAuthorType || (exports.DefaultCartNoteAuthorType = DefaultCartNoteAuthorType = {}));
/**
 * The default action types that could be performed on <<DefaultCartTypes>> `QUOTE`-typed
 * <<Cart,Carts>>, this facilitates <<CartActionAudit>> `ActionType`.
 */
var DefaultQuoteActionType;
(function (DefaultQuoteActionType) {
    /**
     * Used to represent the action of a <<Cart>> from <<DefaultCartTypes>> `STANDARD` to
     * <<DefaultCartTypes>> `QUOTE`.
     */
    DefaultQuoteActionType["DRAFT_QUOTE"] = "DRAFT_QUOTE";
    /**
     * Used to represent the action of requesting the quote to the seller.
     */
    DefaultQuoteActionType["REQUEST_QUOTE"] = "REQUEST_QUOTE";
    /**
     * Used to represent the action of canceling a quote.
     */
    DefaultQuoteActionType["CANCEL_QUOTE"] = "CANCEL_QUOTE";
    /**
     * Used to represent the action of assigning a sales rep to work on the quote.
     */
    DefaultQuoteActionType["ASSIGN_SALES_REP"] = "ASSIGN_SALES_REP";
    /**
     * Used to represent the action of publishing the quote back to the buyer.
     */
    DefaultQuoteActionType["PUBLISH_QUOTE"] = "PUBLISH_QUOTE";
    /**
     * Used to represent the action of rejecting the quote.
     */
    DefaultQuoteActionType["REJECT_QUOTE"] = "REJECT_QUOTE";
    /**
     * Used to represent the action of purchasing the quote.
     */
    DefaultQuoteActionType["PURCHASE_QUOTE"] = "PURCHASE_QUOTE";
    /**
     * Used to represent the action of revising prices in the quote.
     */
    DefaultQuoteActionType["REVISE_PRICES"] = "REVISE_PRICES";
    /**
     * Used to represent the action of modifying an item in the quote.
     */
    DefaultQuoteActionType["MODIFY_ITEM"] = "MODIFY_ITEM";
    /**
     * Used to represent the action of adding an item to the quote.
     */
    DefaultQuoteActionType["ADD_ITEM"] = "ADD_ITEM";
    /**
     * Used to represent the action of removing an item from the quote.
     */
    DefaultQuoteActionType["REMOVE_ITEM"] = "REMOVE_ITEM";
    /**
     * Used to represent an unknown action.
     */
    DefaultQuoteActionType["UNKNOWN"] = "UNKNOWN";
})(DefaultQuoteActionType || (exports.DefaultQuoteActionType = DefaultQuoteActionType = {}));
var DefaultQuoteItemRequestTypes;
(function (DefaultQuoteItemRequestTypes) {
    /**
     * Represents an item.
     *
     * This is typically used for buyers to request an item in the quote by only specifying an item
     * number, etc.
     */
    DefaultQuoteItemRequestTypes["ITEM"] = "ITEM";
    /**
     * Represents a service, e.g. training hours, additional service, etc.
     */
    DefaultQuoteItemRequestTypes["SERVICE"] = "SERVICE";
})(DefaultQuoteItemRequestTypes || (exports.DefaultQuoteItemRequestTypes = DefaultQuoteItemRequestTypes = {}));
//# sourceMappingURL=cart.js.map